/* eslint-disable react/display-name */
import { ProductListItem } from 'app/components/List';
import React, { useContext } from 'react';
import { InspectionContext } from 'app/pages/BookInspection/context/InspectionContext';
import { useDeepMemo } from 'app/hooks/deepMemo';
import { Descriptions, Divider, Table } from 'antd';
import LinkedTemplate from './linked-template';
import { useFetchSampleSize } from 'app/pages/BookInspection/hooks/useFetchSampleSize';
import Panel from 'app/components/Layout/Panel';
import classes from './POItem.module.scss';
import cn from 'classnames';

type Props = {
  item: any;
  onChange: (value: any) => void;
  className?: string;
  bordered?: boolean;
  ordinalNumber?: number;
  hideTemplate?: boolean;
};

const TestPOItem: React.FC<Props> = ({
  item,
  onChange,
  ordinalNumber,
  hideTemplate,
  bordered = true,
  className,
}) => {
  //The purpose of flag is mark templated was loaded at the first time.
  const { inspectionLevel } = useContext(InspectionContext);
  const { inspectionSubPOItems, inspectionAQL } = item;

  const quantity = React.useMemo(() => {
    return inspectionSubPOItems
      .map(({ orderQty }) => Number(orderQty))
      .filter((orderQty) => !isNaN(orderQty))
      .reduce((a, b) => a + b, 0);
  }, [inspectionSubPOItems]);

  const paramsSampleSize = useDeepMemo(() => {
    return {
      qty: quantity,
      inspectionLevel,
      inspectionAqlCritical: inspectionAQL.criticalLevel,
      inspectionAqlMajor: inspectionAQL.criticalMajorLevel,
      inspectionAqlMinor: inspectionAQL.criticalMinorLevel,
    };
  }, [quantity, inspectionLevel, inspectionAQL]);

  const { sampleSize } = useFetchSampleSize(paramsSampleSize);

  return (
    <div style={{ minHeight: 280 }}>
      <ProductListItem
        bordered={bordered}
        order={ordinalNumber || 0}
        className={cn(classes.item, className)}
        image={item.image}
        name={item.name}
        itemNo={item.itemNo || 'N/A'}
        supplier={item.supplierName || 'N/A'}
        factory={item.factoryName || 'No Factory Linkage'}
      >
        <Divider />
        <Descriptions
          column={{
            xs: 1,
            md: 1,
            lg: 2,
            xl: 3,
          }}
          className={classes.descriptions}
          title="Overall"
          labelStyle={{ minWidth: 120 }}
        >
          <Descriptions.Item label="Total Order Qty.">
            {item.inspectionSubPOItems
              .map((item) => Number(item.orderQty))
              .filter((item) => !isNaN(item))
              .reduce((a, b) => a + b, 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Total # of Cartons">
            {item.inspectionSubPOItems
              .map((item) => Number(item.cartonsNo))
              .filter((item) => !isNaN(item))
              .reduce((a, b) => a + b, 0)}
          </Descriptions.Item>

          <Descriptions.Item label="Signed Sample Available">
            {item.isSignSample ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Sample Size">
            {sampleSize ? (
              <div className={classes.groupAQL}>
                <span>
                  <p>Critical</p>
                  <p>{sampleSize?.critical?.sampleSize || 0}</p>
                </span>
                <span>
                  <p>Major</p>
                  <p>{sampleSize?.major?.sampleSize || 0}</p>
                </span>
                <span>
                  <p>Minor</p>
                  <p>{sampleSize?.minor?.sampleSize || 0}</p>
                </span>
              </div>
            ) : null}
          </Descriptions.Item>
          <Descriptions.Item label="AQL">
            <div className={classes.groupAQL}>
              <span>
                <p>Critical</p>
                <p>{item.inspectionAQL.criticalLevel || 0}</p>
              </span>
              <span>
                <p>Major</p>
                <p>{item.inspectionAQL.criticalMajorLevel || 0}</p>
              </span>
              <span>
                <p>Minor</p>
                <p>{item.inspectionAQL.criticalMinorLevel || 0}</p>
              </span>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Defectives Accepted">
            {sampleSize && (
              <div className={classes.groupAQL}>
                <span>
                  <p>Critical</p>
                  <p>{sampleSize?.critical?.acceptanceLevel ?? 0}</p>
                </span>
                <span>
                  <p>Major</p>
                  <p>{sampleSize?.major?.acceptanceLevel ?? 0}</p>
                </span>
                <span>
                  <p>Minor</p>
                  <p>{sampleSize?.minor?.acceptanceLevel ?? 0}</p>
                </span>
              </div>
            )}
          </Descriptions.Item>
        </Descriptions>
        <div className={classes.wrapperTable}>
          <span className={classes.title}>PO Detail:</span>
          <Table
            rowKey={(item, itemIndex) => item.id || itemIndex}
            pagination={false}
            className={classes.table}
            columns={[
              {
                title: 'PO #',
                dataIndex: 'poNo',
              },
              {
                title: 'PO Qty',
                dataIndex: 'orderQty',
              },
              {
                title: 'PO # of Cartons',
                dataIndex: 'cartonsNo',
              },
              {
                title: 'Customer Name',
                dataIndex: 'clientName',
              },
              {
                title: 'Customer PO#',
                dataIndex: 'clientPONo',
              },
            ]}
            dataSource={item.inspectionSubPOItems}
          />
        </div>
      </ProductListItem>
      {!hideTemplate && (
        <Panel title="Linked Template">
          <LinkedTemplate
            productId={item.id}
            inspectionCriteria={item.inspectionCriteria}
            onChange={(newTemplate) => {
              onChange({
                ...item,
                inspectionCriteria: {
                  ...newTemplate,
                },
              });
            }}
          />
        </Panel>
      )}
    </div>
  );
};

export default React.memo(TestPOItem);
